import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AIQueryPage from './pages/AIQueryPage';
import ConnectionsPage from './pages/ConnectionsPage';
import HistoryPage from './pages/HistoryPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import LearningPage from './pages/LearningPage';
import TwoFASetupPage from './pages/TwoFASetupPage';
import TwoFAVerifyPage from './pages/TwoFAVerifyPage';
import SecurityPage from './pages/SecurityPage';
import BlockedPage, { useIPCheck } from './pages/BlockedPage';
import DownloadsPage from './pages/DownloadsPage';
import CustomDashboardPage from './pages/CustomDashboardPage';
import ScheduledReportsPage from './pages/ScheduledReportsPage';
import LookupTablesPage from './pages/LookupTablesPage';
import LearningsInspectorPage from './pages/LearningsInspectorPage';

const AppRoutes = () => {
  const { user, loading, twoFAState, isAdmin } = useAuth();
  const { blocked, checking } = useIPCheck();

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FDFBF7 0%, #F5F0E8 100%)' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#D4AF37] border-t-transparent" />
      </div>
    );
  }

  // IP blocked
  if (blocked) {
    return <BlockedPage />;
  }

  // Not logged in at all
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Logged in but needs 2FA setup
  if (twoFAState === 'setup_required') {
    return (
      <Routes>
        <Route path="/setup-2fa" element={<TwoFASetupPage />} />
        <Route path="*" element={<Navigate to="/setup-2fa" replace />} />
      </Routes>
    );
  }

  // Logged in but needs 2FA verification
  if (twoFAState === 'verify_required') {
    return (
      <Routes>
        <Route path="/verify-2fa" element={<TwoFAVerifyPage />} />
        <Route path="*" element={<Navigate to="/verify-2fa" replace />} />
      </Routes>
    );
  }

  // Fully authenticated - render full app
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/my-dashboard" element={<CustomDashboardPage />} />
        <Route path="/query" element={<AIQueryPage />} />
        <Route path="/learning" element={<LearningPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        <Route path="/scheduled-reports" element={<ScheduledReportsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {isAdmin && <Route path="/connections" element={<ConnectionsPage />} />}
        {isAdmin && <Route path="/users" element={<UsersPage />} />}
        {isAdmin && <Route path="/security" element={<SecurityPage />} />}
        {isAdmin && <Route path="/lookup-tables" element={<LookupTablesPage />} />}
        {isAdmin && <Route path="/learnings-inspector" element={<LearningsInspectorPage />} />}
      </Route>
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
